import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("beekeep_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => localStorage.removeItem("beekeep_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password, remember_me = false) => {
    const trusted_device_token = localStorage.getItem("beekeep_trusted_device_token") || null;
    const { data } = await api.post("/auth/login", { email, password, remember_me, trusted_device_token });
    if (data?.require_2fa) {
      // do NOT store token; caller (Login page) will render the 2FA challenge
      return { require_2fa: true, otp_token: data.otp_token, email_hint: data.email_hint };
    }
    localStorage.setItem("beekeep_token", data.token);
    // refetch /me to populate is_admin + impersonator
    const { data: full } = await api.get("/auth/me");
    setUser(full);
    return full;
  };

  const complete2fa = async (otp_token, method, code, trust_device = false) => {
    const { data } = await api.post("/auth/2fa/verify", { otp_token, method, code, trust_device });
    localStorage.setItem("beekeep_token", data.token);
    if (data.trusted_device_token) {
      localStorage.setItem("beekeep_trusted_device_token", data.trusted_device_token);
    }
    const { data: full } = await api.get("/auth/me");
    setUser(full);
    return full;
  };

  const register = async (email, password, name, remember_me = false, guard = {}) => {
    const { data } = await api.post("/auth/register", { email, password, name, remember_me, ...guard });
    localStorage.setItem("beekeep_token", data.token);
    const { data: full } = await api.get("/auth/me");
    setUser(full);
    return full;
  };

  const logout = () => {
    localStorage.removeItem("beekeep_token");
    localStorage.removeItem("beekeep_admin_backup");
    setUser(null);
  };

  const startImpersonation = async (targetUserId) => {
    const { data } = await api.post(`/admin/users/${targetUserId}/impersonate`);
    const adminToken = localStorage.getItem("beekeep_token");
    localStorage.setItem("beekeep_admin_backup", adminToken);
    localStorage.setItem("beekeep_token", data.token);
    const { data: full } = await api.get("/auth/me");
    setUser(full);
    return full;
  };

  const stopImpersonation = async () => {
    const backup = localStorage.getItem("beekeep_admin_backup");
    if (!backup) return;
    localStorage.setItem("beekeep_token", backup);
    localStorage.removeItem("beekeep_admin_backup");
    const { data: full } = await api.get("/auth/me");
    setUser(full);
  };

  const refreshMe = async () => {
    const { data: full } = await api.get("/auth/me");
    setUser(full);
    return full;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, complete2fa, register, logout, startImpersonation, stopImpersonation, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
